import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CurrencyDollarIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PrinterIcon,
  EyeIcon,
  BanknotesIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  UsersIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { DateDisplay } from '../../components/Forms';
import { FormField, Input, Textarea, FormCard } from '../../components/Forms';

interface CashClosingData {
  record: {
    _id: string;
    date: string;
    status: 'open' | 'closed' | 'reviewed';
    revenue: {
      bookings: number;
      transportation: number;
      accommodation: number;
      additional: number;
      total: number;
    };
    expenses: {
      fuel: number;
      maintenance: number;
      salaries: number;
      accommodation: number;
      other: number;
      total: number;
    };
    profit: {
      gross: number;
      net: number;
      margin: number;
    };
    cashBox: {
      opening: number;
      closing: number;
      cash: number;
      card: number;
      transfer: number;
      difference: number;
    };
    stats: {
      totalBookings: number;
      confirmedBookings: number;
      cancelledBookings: number;
      totalPassengers: number;
    };
    closedBy?: {
      fullName: string;
    };
    closedAt?: string;
    notes: Array<{
      text: string;
      createdBy: string;
      createdAt: string;
    }>;
  };
  bookings: any[];
  cashTransactions: {
    income: number;
    expense: number;
    total: number;
  };
  summary: {
    totalBookings: number;
    totalRevenue: number;
    cashRevenue: number;
    totalPassengers: number;
  };
}

const CashClosing: React.FC = () => {
  const queryClient = useQueryClient();
  const [showClosingForm, setShowClosingForm] = useState(false);
  const [showReopenForm, setShowReopenForm] = useState(false);
  const [closingCash, setClosingCash] = useState<number>(0);
  const [closingNotes, setClosingNotes] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Fetch today's cash closing data
  const { data: cashData, isLoading, error, refetch } = useQuery<CashClosingData>({
    queryKey: ['cash-closing', 'today'],
    queryFn: () => apiService.getTodayCashClosing(),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch cash closing history
  const { data: historyData } = useQuery({
    queryKey: ['cash-closing', 'history'],
    queryFn: () => apiService.getCashClosingHistory({ limit: 10 }),
    enabled: showHistory
  });

  // Close cash mutation
  const closeCashMutation = useMutation({
    mutationFn: (data: { closingCash: number; notes?: string }) => 
      apiService.closeCash(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-closing'] });
      queryClient.invalidateQueries({ queryKey: ['cash-closing', 'history'] });
      setShowClosingForm(false);
      setClosingCash(0);
      setClosingNotes('');
      refetch(); // Refresh the current data
    }
  });

  // Reopen cash mutation
  const reopenCashMutation = useMutation({
    mutationFn: (data: { reason: string }) => 
      apiService.reopenCash(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-closing'] });
      queryClient.invalidateQueries({ queryKey: ['cash-closing', 'history'] });
      setShowReopenForm(false);
      setReopenReason('');
      refetch(); // Refresh the current data
    }
  });

  // Auto-calculate expected closing cash
  useEffect(() => {
    if (cashData) {
      const expectedClosing = cashData.record.cashBox.opening + cashData.cashTransactions.total;
      setClosingCash(expectedClosing);
    }
  }, [cashData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const formatTime = (dateString: string) => {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('ar-SA-u-nu-latn', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCloseCash = () => {
    closeCashMutation.mutate({
      closingCash,
      notes: closingNotes || undefined
    });
  };

  const handleReopenCash = () => {
    if (!reopenReason.trim()) return;
    reopenCashMutation.mutate({ reason: reopenReason });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">خطأ في تحميل البيانات</h3>
          <button
            onClick={() => refetch()}
            className="btn-primary"
          >
            <ArrowPathIcon className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  if (!cashData || !cashData.record) return null;

  const { record, summary, cashTransactions } = cashData;
  const isClosed = record.status === 'closed';
  const expectedClosing = (record.cashBox?.opening || 0) + (cashTransactions?.total || 0);
  const difference = (record.cashBox?.closing || 0) - expectedClosing;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إغلاق الكاشير اليومي</h1>
          <p className="text-gray-600 mt-1">
            <DateDisplay value={record.date} className="inline-flex" /> - {isClosed ? 'مغلق' : 'مفتوح'}
          </p>
        </div>
        <div className="flex space-x-3 space-x-reverse">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="btn-secondary"
          >
            <EyeIcon className="w-4 h-4 ml-2" />
            {showHistory ? 'إخفاء السجل' : 'عرض السجل'}
          </button>
          <button
            onClick={() => refetch()}
            className="btn-secondary"
          >
            <ArrowPathIcon className="w-4 h-4 ml-2" />
            تحديث
          </button>
        </div>
      </div>

      {/* Status Alert */}
      <div className={`rounded-lg p-4 ${
        isClosed 
          ? 'bg-green-50 border border-green-200' 
          : 'bg-yellow-50 border border-yellow-200'
      }`}>
        <div className="flex items-center">
          {isClosed ? (
            <CheckCircleIcon className="w-6 h-6 text-green-600 ml-3" />
          ) : (
            <ClockIcon className="w-6 h-6 text-yellow-600 ml-3" />
          )}
          <div>
            <h3 className="font-medium text-gray-900">
              {isClosed ? 'تم إغلاق الكاشير' : 'الكاشير مفتوح'}
            </h3>
            <p className="text-sm text-gray-600">
              {isClosed 
                ? `أُغلق بواسطة ${record.closedBy?.fullName} في ${formatTime(record.closedAt!)}`
                : 'يرجى إغلاق الكاشير في نهاية اليوم'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Daily Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <UsersIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">إجمالي الحجوزات</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.totalBookings || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">إجمالي الإيرادات</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary?.totalRevenue || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <BanknotesIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">إيرادات نقدية</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary?.cashRevenue || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <UsersIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">إجمالي المسافرين</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.totalPassengers || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cash Box Details */}
      <FormCard 
        title="تفاصيل الصندوق النقدي" 
        icon={<BanknotesIcon className="w-5 h-5 text-primary-600" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">الرصيد الافتتاحي</label>
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(record.cashBox.opening)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">المعاملات النقدية</label>
            <div className="text-lg font-semibold text-green-600">
              +{formatCurrency(cashTransactions.total)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">الرصيد المتوقع</label>
            <div className="text-lg font-semibold text-blue-600">
              {formatCurrency(expectedClosing)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">الرصيد الختامي الفعلي</label>
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(record.cashBox.closing)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">الفرق</label>
            <div className={`text-lg font-semibold ${
              difference === 0 ? 'text-green-600' : 
              difference > 0 ? 'text-blue-600' : 'text-red-600'
            }`}>
              {difference === 0 ? 'متطابق' : 
               difference > 0 ? `+${formatCurrency(Math.abs(difference))}` : 
               `-${formatCurrency(Math.abs(difference))}`}
            </div>
          </div>
        </div>

        {/* Payment Methods Breakdown */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-medium text-gray-900 mb-4">توزيع طرق الدفع</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <BanknotesIcon className="w-5 h-5 text-green-600 ml-2" />
                <span className="text-sm font-medium text-gray-700">نقدي</span>
              </div>
              <span className="font-semibold text-gray-900">
                {formatCurrency(record.cashBox.cash)}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <CreditCardIcon className="w-5 h-5 text-blue-600 ml-2" />
                <span className="text-sm font-medium text-gray-700">بطاقة</span>
              </div>
              <span className="font-semibold text-gray-900">
                {formatCurrency(record.cashBox.card)}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <BuildingLibraryIcon className="w-5 h-5 text-purple-600 ml-2" />
                <span className="text-sm font-medium text-gray-700">تحويل</span>
              </div>
              <span className="font-semibold text-gray-900">
                {formatCurrency(record.cashBox.transfer)}
              </span>
            </div>
          </div>
        </div>
      </FormCard>

      {/* Bookings Statistics */}
      <FormCard 
        title="إحصائيات الحجوزات" 
        icon={<ReceiptPercentIcon className="w-5 h-5 text-primary-600" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{record.stats.confirmedBookings}</div>
            <div className="text-sm text-gray-600">حجوزات مؤكدة</div>
          </div>

          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {record.stats.totalBookings - record.stats.confirmedBookings - record.stats.cancelledBookings}
            </div>
            <div className="text-sm text-gray-600">حجوزات معلقة</div>
          </div>

          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{record.stats.cancelledBookings}</div>
            <div className="text-sm text-gray-600">حجوزات ملغية</div>
          </div>
        </div>
      </FormCard>

      {/* Action Buttons */}
      {!isClosed ? (
        <div className="flex justify-end space-x-3 space-x-reverse">
          <button
            onClick={() => setShowClosingForm(true)}
            className="btn-primary"
            disabled={closeCashMutation.isPending}
          >
            <BanknotesIcon className="w-4 h-4 ml-2" />
            إغلاق الكاشير
          </button>
        </div>
      ) : (
        <div className="flex justify-end space-x-3 space-x-reverse">
          <button
            onClick={() => setShowReopenForm(true)}
            className="btn-secondary"
            disabled={reopenCashMutation.isPending}
          >
            <ArrowPathIcon className="w-4 h-4 ml-2" />
            إعادة فتح الكاشير
          </button>
          <button
            onClick={() => window.print()}
            className="btn-secondary"
          >
            <PrinterIcon className="w-4 h-4 ml-2" />
            طباعة التقرير
          </button>
        </div>
      )}

      {/* Cash Closing Form Modal */}
      {showClosingForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowClosingForm(false)}></div>

            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">إغلاق الكاشير</h3>
              
              <div className="space-y-4">
                <FormField label="المبلغ النقدي الفعلي" required>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={closingCash}
                    onChange={(e) => setClosingCash(parseFloat(e.target.value) || 0)}
                    placeholder="أدخل المبلغ النقدي الفعلي"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    المبلغ المتوقع: {formatCurrency(expectedClosing)}
                  </p>
                </FormField>

                <FormField label="ملاحظات (اختياري)">
                  <Textarea
                    rows={3}
                    value={closingNotes}
                    onChange={(e) => setClosingNotes(e.target.value)}
                    placeholder="أي ملاحظات حول الإغلاق..."
                  />
                </FormField>

                {Math.abs(closingCash - expectedClosing) > 0 && (
                  <div className={`p-3 rounded-lg ${
                    closingCash - expectedClosing > 0 
                      ? 'bg-blue-50 border border-blue-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <p className="text-sm font-medium">
                      {closingCash - expectedClosing > 0 ? 'زيادة' : 'نقص'} في المبلغ: {' '}
                      <span className={closingCash - expectedClosing > 0 ? 'text-blue-600' : 'text-red-600'}>
                        {formatCurrency(Math.abs(closingCash - expectedClosing))}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  onClick={() => setShowClosingForm(false)}
                  className="btn-secondary"
                  disabled={closeCashMutation.isPending}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleCloseCash}
                  className="btn-primary"
                  disabled={closeCashMutation.isPending}
                >
                  {closeCashMutation.isPending ? 'جاري الإغلاق...' : 'إغلاق الكاشير'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Cash Form Modal */}
      {showReopenForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowReopenForm(false)}></div>

            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">إعادة فتح الكاشير</h3>
              
              <div className="space-y-4">
                <FormField label="سبب إعادة الفتح" required>
                  <Textarea
                    rows={3}
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    placeholder="اذكر سبب إعادة فتح الكاشير..."
                  />
                </FormField>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    تحذير: إعادة فتح الكاشير سيسمح بإجراء معاملات جديدة لهذا اليوم
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  onClick={() => setShowReopenForm(false)}
                  className="btn-secondary"
                  disabled={reopenCashMutation.isPending}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleReopenCash}
                  className="btn-primary"
                  disabled={reopenCashMutation.isPending || !reopenReason.trim()}
                >
                  {reopenCashMutation.isPending ? 'جاري إعادة الفتح...' : 'إعادة فتح'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Section */}
      {showHistory && historyData && (
        <FormCard 
          title="سجل الإغلاقات السابقة" 
          icon={<CalendarDaysIcon className="w-5 h-5 text-primary-600" />}
        >
          <div className="space-y-3">
            {!historyData.data || historyData.data.length === 0 ? (
              <p className="text-center text-gray-500 py-8">لا توجد إغلاقات سابقة</p>
            ) : (
              historyData.data.map((record: any) => (
                <div key={record._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">
                      <DateDisplay value={record.date} />
                    </div>
                    <div className="text-sm text-gray-600">
                      {record.status === 'closed' ? 'مغلق' : 'مفتوح'} - 
                      إجمالي الإيرادات: {formatCurrency(record.revenue.total)}
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="text-sm text-gray-600">
                      {record.closedBy?.fullName || 'غير محدد'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {record.closedAt ? formatTime(record.closedAt) : ''}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </FormCard>
      )}

      {/* Notes Section */}
      {record.notes && record.notes.length > 0 && (
        <FormCard 
          title="الملاحظات" 
          icon={<DocumentTextIcon className="w-5 h-5 text-primary-600" />}
        >
          <div className="space-y-3">
            {record.notes.map((note, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-900">{note.text}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <DateDisplay value={note.createdAt} showTime />
                </p>
              </div>
            ))}
          </div>
        </FormCard>
      )}
    </div>
  );
};

export default CashClosing; 